import { Suspense, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/base/ScrollToTop';
import { SEO } from '../../utils/seo';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "お問い合わせ",
    "description": "おかやま就労支援事業所へのお問い合わせページ",
    "url": `${siteUrl}/contact`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "おかやま就労支援事業所",
      "telephone": "+81-86-012-3456",
      "email": "info@okayama-employment-support.example.com"
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    furigana: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
    agree: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.furigana || !formData.email || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    if (!formData.agree) {
      alert('プライバシーポリシーに同意してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('furigana', formData.furigana);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('inquiryType', formData.inquiryType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5g70u8f0tcnoht87520', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          furigana: '',
          email: '',
          phone: '',
          inquiryType: '',
          message: '',
          agree: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEO
        title="お問い合わせ｜見学・相談随時受付中｜おかやま就労支援事業所"
        description="おかやま就労支援事業所へのお問い合わせページ。見学・相談随時受付中。お電話またはお問い合わせフォームからお気軽にご連絡ください。TEL:086-012-3456（受付時間9:00-18:00、日祝休）。岡山市北区の就労支援事業所。"
        keywords="お問い合わせ,見学,相談,電話,フォーム,岡山市"
        schema={schema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-bg-ivory">
        <Header />
        <main>
          {/* Hero Section */}
          <section className="relative h-96 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <img
                src="https://public.readdy.ai/ai/img_res/09d1d97e0296b73788eb4dfd9ca05fe0.jpg"
                alt="お問い合わせ"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            </div>
            <div className="relative z-10 text-center text-white space-y-4">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">
                お問い合わせ
              </h1>
              <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto px-4">
                ご質問やご相談など、お気軽にお問い合わせください。
                <br />
                担当スタッフが丁寧にご対応いたします。
              </p>
            </div>
          </section>

          {/* Form Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl shadow-2xl p-6 sm:p-10 lg:p-12 space-y-6"
              >
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-text-dark font-bold mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-bg-beige rounded-lg border-2 border-transparent focus:border-primary focus:outline-none transition-colors"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* Furigana */}
                <div>
                  <label htmlFor="furigana" className="block text-text-dark font-bold mb-2">
                    フリガナ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="furigana"
                    name="furigana"
                    value={formData.furigana}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-bg-beige rounded-lg border-2 border-transparent focus:border-primary focus:outline-none transition-colors"
                    placeholder="ヤマダ タロウ"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-text-dark font-bold mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-bg-beige rounded-lg border-2 border-transparent focus:border-primary focus:outline-none transition-colors"
                    placeholder="example@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-text-dark font-bold mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-bg-beige rounded-lg border-2 border-transparent focus:border-primary focus:outline-none transition-colors"
                    placeholder="090-1234-5678"
                  />
                </div>

                {/* Inquiry Type */}
                <div>
                  <label htmlFor="inquiryType" className="block text-text-dark font-bold mb-2">
                    お問い合わせ種別
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-bg-beige rounded-lg border-2 border-transparent focus:border-primary focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="見学希望">見学希望</option>
                    <option value="利用相談">利用相談</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-text-dark font-bold mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-bg-beige rounded-lg border-2 border-transparent focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  ></textarea>
                  <p className="text-text-medium text-sm mt-1 text-right">
                    {formData.message.length} / 500文字
                  </p>
                </div>

                {/* Privacy Policy */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agree"
                    name="agree"
                    checked={formData.agree}
                    onChange={handleChange}
                    required
                    className="mt-1 mr-3 w-5 h-5 cursor-pointer"
                  />
                  <label htmlFor="agree" className="text-text-medium text-sm cursor-pointer">
                    プライバシーポリシーに同意します <span className="text-red-500">*</span>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-3/5 mx-auto block px-8 py-4 bg-primary text-white rounded-full font-bold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center">
                    お問い合わせを受け付けました。ありがとうございます。
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
                    送信に失敗しました。もう一度お試しください。
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* Phone Contact Section */}
          <section className="py-16 sm:py-20 bg-bg-beige">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-text-dark mb-6">
                お電話でのお問い合わせ
              </h2>
              <a
                href="tel:0860123456"
                className="inline-flex items-center text-primary text-3xl sm:text-4xl font-bold hover:text-primary/70 transition-colors mb-4"
              >
                <i className="ri-phone-line mr-3 w-10 h-10 flex items-center justify-center"></i>
                086-012-3456
              </a>
              <p className="text-text-medium">受付時間: 9:00 - 18:00（日祝休）</p>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </Suspense>
  );
};

export default ContactPage;
